import { Contract, RobotSystem } from "@/interfaces"
import { DateTime } from "luxon"
import { CONTRACT_FEATURE_TYPE } from "@/enums"
import { getActiveFeature, nearestServiceIssueDate } from "@/data/systems"

const getMaxDate = (dates: (DateTime | null | undefined)[]) => {
  const filteredDates = dates.filter((d): d is DateTime => d != null)
  return DateTime.max(...filteredDates)
}

export const getKeyDates = (system: RobotSystem | null) => {
  const now = DateTime.now().startOf('day')

  if (!system) {
    return {
      now,
      activeSince: now,
      startOfWarranty: now,
      endOfLegalWarranty: now,
      endOfWarranty: now,
      startOfContract: null,
      endOfContract: null,
      serviceContractStart: null,
      nearestService: null,
      warrantyExtensionFeatureEnd: null,
      hasWarrantyExtensionFeature: false,
      warrantyExtensionFeatureExpired: false,
      legalWarrantyExpired: false,
      contractTitle: undefined,
      maxDisplayDate: now.plus({ days: 90 }),
      maxDays: 90
    }
  }

  const warrantyExtensionFeature = system ?
    getActiveFeature(system, CONTRACT_FEATURE_TYPE.WARRANTY_EXTENSION)
    : undefined

  const activeSince = system.assignedAt
  // this was value previously called "start"
  // primary value was warrantyExtensionFeature?.featureStart
  // then as second option system.value.buildDates.robotArm

  const endOfLegalWarranty = activeSince.plus({ years: 1 })

  const startOfWarranty = warrantyExtensionFeature?.featureStart
        || system.serviceContractStart
        || now

  const warrantyExtensionFeatureEnd = warrantyExtensionFeature?.featureEnd ?? null
  const endOfWarranty = warrantyExtensionFeatureEnd
        || endOfLegalWarranty
  const isActive = (c: Contract) =>
    c.start <= now && (!c.end || c.end >= now)

  const carePlusContract = warrantyExtensionFeature?.contracts.find(c =>
    c.title.includes('Care Plus') && isActive(c)
  )

  const careContract = warrantyExtensionFeature?.contracts.find(c =>
    c.title.includes('Care') && !c.title.includes('Care Plus') && isActive(c)
  )

  const startOfContract = carePlusContract?.start ?? careContract?.start ?? null

  const endOfContract = carePlusContract?.end ?? careContract?.end ?? null

  const serviceContractStart = system.serviceContractStart ?? null

  const nearestService = system ?
    nearestServiceIssueDate(system, false)
  : null

  const hasWarrantyExtensionFeature = !!warrantyExtensionFeature
  const warrantyExtensionFeatureExpired = !!(warrantyExtensionFeatureEnd && warrantyExtensionFeatureEnd.diffNow('days').days < 0)
  const legalWarrantyExpired = endOfLegalWarranty.diffNow('days').days < 0
  const contractTitle = carePlusContract?.title ?? careContract?.title ?? undefined

  const maxDisplayDate = getMaxDate([
    endOfLegalWarranty, // Default max display
    endOfWarranty,
    nearestService,
    endOfContract,
    now.plus({ months: 3 }), // Always show 3 months ahead
  ])

  const maxDays = maxDisplayDate.diff(activeSince, 'days').days

  return {
    now,
    activeSince,
    startOfWarranty,
    endOfLegalWarranty,
    endOfWarranty,
    warrantyExtensionFeatureEnd,
    startOfContract,
    endOfContract,
    serviceContractStart,
    nearestService,
    hasWarrantyExtensionFeature,
    warrantyExtensionFeatureExpired,
    legalWarrantyExpired,
    contractTitle,
    maxDisplayDate,
    maxDays,
  }
}
